import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, StaticProvider } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AbTestVersions } from '@core/services/abtest.webservice';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { Store } from '@ngxs/store';
import { AbTestState, AbTestStateModel } from '@stores/abtest/abtest.state';
import { DossierState } from '@stores/dossier/dossier.state';
import { SessionState } from '@stores/session/session.state';
import { Dossier } from '@wizbii/models';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class BetaAccessGuard implements CanActivateChild {
  constructor(
    readonly store: Store,
    readonly router: Router,
    @Inject(PLATFORM_ID) private readonly platformId: StaticProvider[]
  ) {}

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    if (isPlatformServer(this.platformId)) {
      return of(true);
    }

    return combineLatest([
      this.store.select(AbTestState),
      this.store.select(DossierState.dossiers),
      this.store.select(SessionState),
    ]).pipe(
      filter(
        ([abTest, dossiers, sessionState]) =>
          !!abTest?.version &&
          !!sessionState &&
          (abTest.userType === 'visitor' || (abTest.userType === 'member' && !!dossiers))
      ),
      map(([abTest, dossiers]: [AbTestStateModel, Dossier[], SessionState]) => {
        if (
          (abTest.userType === 'member' && dossiers?.find((dossier) => !dossier.isNme)) ||
          this.store.selectSnapshot(SessionState.isB2BUser)
        ) {
          return this.transformBetaToDefaultRoute(state.url);
        }

        if (abTest.campaignId === environment.abTest.campaignId && abTest.version === AbTestVersions.B) {
          return true;
        }

        if (state.url === `/${FeaturesRoutingEnum.Beta}`) {
          return this.router.parseUrl('/');
        }

        return this.router.parseUrl(state.url.replace(`/${FeaturesRoutingEnum.Beta}`, ''));
      })
    );
  }

  transformBetaToDefaultRoute(url: string): UrlTree {
    return url === `/${FeaturesRoutingEnum.Beta}`
      ? this.router.parseUrl('/')
      : this.router.parseUrl(url.replace(`/${FeaturesRoutingEnum.Beta}`, ''));
  }
}
